// Pilot mode on the screens (CLAUDE.md rule 27, migration 024).
//
// Two marks, and they answer different questions:
//   - PilotBanner: IS PILOT MODE ON NOW. Chrome on both shells, while the window is open. Read from
//     GET /pilot - the same function the gate asks - so the screen cannot say "on" while the
//     server says "off", or the reverse.
//   - PilotBadge: IS THIS RECORD A PILOT RECORD. On every pilot run, reading and verification, for
//     ever, from the row's own IsPilot. It never depends on the window: a pilot record stays one.
//
// ⚠ NO VERDICT COLOUR. Red, amber and green mean a quality result in this app; a pilot mark that
// borrowed amber would read as ACTION. The banner is the neutral ramp inverted, the badge a dashed
// outline - both loud, neither a verdict.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

export interface PilotMode {
  active: boolean;
  until: string | null;
  state: 'off' | 'active' | 'ended' | 'invalid' | 'too_far';
  message: string;
}

export interface PilotStatus {
  /** null until the server has answered; stays null on a failed read (rule 15: unknown, not off). */
  mode: PilotMode | null;
  error: boolean;
  reload: () => void;
}

/** Re-asked every five minutes, so a window that closes mid-shift takes its banner with it. */
const REFRESH_MS = 5 * 60_000;

export function usePilot(): PilotStatus {
  const [mode, setMode] = useState<PilotMode | null>(null);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const ask = () =>
      (api.get('/pilot') as Promise<PilotMode>)
        .then((m) => {
          if (cancelled) return;
          setMode(m && typeof m.active === 'boolean' ? m : null);
          setError(!(m && typeof m.active === 'boolean'));
        })
        .catch(() => {
          if (cancelled) return;
          setMode(null);
          setError(true);
        });
    void ask();
    const timer = setInterval(() => void ask(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { mode, error, reload };
}

/**
 * When the window closes, in Brisbane words. A bare-date setting closes at 00:00 the next day,
 * which reads as "until 7 Nov, 12:00 am" for a pilot that ends on the 6th - so midnight is said
 * as the end of the day before.
 */
export function pilotUntilText(until: string | null): string {
  if (!until) return 'no end date';
  const t = new Date(until);
  if (Number.isNaN(t.getTime())) return 'no end date';
  const zone = { timeZone: 'Australia/Brisbane' } as const;
  const hm = new Intl.DateTimeFormat('en-AU', { ...zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(t);
  if (hm === '00:00') {
    const dayBefore = new Date(t.getTime() - 1);
    return `end of ${new Intl.DateTimeFormat('en-AU', { ...zone, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(dayBefore)}`;
  }
  return new Intl.DateTimeFormat('en-AU', { ...zone, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(t);
}

/** Chrome: shown while pilot mode is on, and a plain line when whether it is on cannot be read. */
export function PilotBanner({ pilot, className }: { pilot: PilotStatus; className?: string }) {
  if (pilot.mode?.active) {
    return (
      <div
        role="status"
        className={cn(
          'flex flex-none flex-wrap items-center gap-x-(--s3) bg-(--fg) px-(--s4) py-(--s2) text-(length:--fs-sm) font-semibold text-(--card)',
          className,
        )}
      >
        <span>PILOT — not a controlled record (until {pilotUntilText(pilot.mode.until)})</span>
        <span className="font-normal">Competency and meter calibration are not enforced on pilot runs.</span>
      </div>
    );
  }
  if (pilot.mode === null && pilot.error) {
    return (
      <div
        role="status"
        className={cn(
          'flex flex-none flex-wrap items-center gap-x-(--s3) border-b border-(--border) bg-(--subtle) px-(--s4) py-(--s1) text-(length:--fs-sm) text-(--muted)',
          className,
        )}
      >
        <span>Could not read whether pilot mode is on. Pilot runs still show their PILOT badge.</span>
        <button type="button" onClick={pilot.reload} className="underline">
          Ask again
        </button>
      </div>
    );
  }
  return null;
}

/** On every pilot run, reading and verification. Renders nothing for a controlled record. */
export function PilotBadge({ isPilot, size = 'sm', className }: { isPilot: unknown; size?: 'sm' | 'lg'; className?: string }) {
  if (isPilot !== true && isPilot !== 1) return null;
  return (
    <span
      title="Pilot record: competency and meter calibration were not enforced. Not a controlled record."
      className={cn(
        'inline-flex items-center rounded-(--radius-sm) border-2 border-dashed border-(--fg) bg-(--subtle) font-bold tracking-wide text-(--fg)',
        size === 'lg' ? 'px-(--s2) py-[2px] text-(length:--fs-sm)' : 'px-[6px] text-(length:--fs-xs)',
        className,
      )}
    >
      PILOT
    </span>
  );
}
